import { useCallback, useContext, useRef, useMemo } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';
import {
  useParticipantTrackedState,
  ParticipantLiveStateContext,
  PARTICIPANT_STATE_KEYS,
  EMPTY_PARTICIPANT_STATE
} from './participantStateContext';
import { useStableEvent } from '../meeting/meetingProviderContextDef';
import { events, rejectUnavailable } from '../utils';

const NOOP = () => {};

/**
 *
 * @param {string} participantId
 * @param {{
 *  onStreamDisabled?: Function,
 *  onStreamEnabled?: Function,
 *  onMediaStatusChanged?: Function,
 *  onVideoQualityChanged?: Function,
 *  onStreamPaused?: Function,
 *  onStreamResumed?: Function,
 *  onProducerAdded?: Function,
 *  onProducerRemoved?: Function,
 *  onConsumerAdded?: Function,
 *   onConsumerRemoved?: Function
 * }} options
 *
 * @returns {{
 *   displayName: string;
 *   participant: Participant;
 *   webcamStream: Stream;
 *   micStream: Stream;
 *   screenShareStream: Stream;
 *   screenShareAudioStream: Stream;
 *   webcamOn: boolean;
 *   micOn: boolean;
 *   screenShareOn: boolean;
 *   screenShareAudioOn: boolean;
 *   isLocal: boolean;
 *   isActiveSpeaker: boolean;
 *   isMainParticipant: boolean;
 *   pinState: any;
 *   mode: "SEND_AND_RECV" | "SIGNALLING_ONLY" | "RECV_ONLY";
 *   metaData: object;
 *   consumeMicStreams: () => Promise<void>;
 *   consumeWebcamStreams: () => Promise<void>;
 *   stopConsumingMicStreams: () => Promise<void>;
 *   stopConsumingWebcamStreams: () => Promise<void>;
 *   setQuality: (quality: "low" | "med" | 'high') => Promise<void>;
 *   setScreenShareQuality: (quality: "low" | "med" | 'high') => Promise<void>;
 *   setViewPort: (width: number, height: number) => void;
 *   enableMic: () => Promise<void>;
 *   disableMic: () => Promise<void>;
 *   enableWebcam: () => Promise<void>;
 *   disableWebcam: () => Promise<void>;
 *   remove: () => Promise<void>;
 *   captureImage: ({height,width}) => Promise<string | null>;
 *   pin: (data: "SHARE_AND_CAM" | "CAM" | "SHARE") => Promise<void>;
 *   unpin: (data: "SHARE_AND_CAM" | "CAM" | "SHARE") => Promise<void>;
 *   getShareAudioStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object
 * }]>;
 *   getAudioStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object
 * }]>;
 * getVideoStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object,
 *   currentSpatialLayer: number,
 *   currentTemporalLayer: number,
 *   preferredSpatialLayer: number,
 *   preferredTemporalLayer: number
 * }]>;
 * getShareStats: () => Promise<[{
 *   bitrate: number,
 *   rtt: number,
 *   network:String,
 *   codec: String,
 *   jitter: number,
 *   limitation: Object,
 *   totalPackets: number,
 *   packetsLost: number,
 *   concealmentEvents: number,
 *   insertedSamplesForDecelaration: number,
 *   removedSamplesForAccelaration: number,
 *   size:Object,
 *   currentSpatialLayer: number,
 *   currentTemporalLayer: number,
 *   preferredSpatialLayer: number,
 *   preferredTemporalLayer: number
 * }]>;
 *}}
 *
 */
const useParticipant = (
  participantId,
  {
    onStreamEnabled = NOOP,
    onStreamDisabled = NOOP,
    onMediaStatusChanged = NOOP,
    onVideoQualityChanged = NOOP,
    onStreamPaused = NOOP,
    onStreamResumed = NOOP,
    onProducerAdded = NOOP,
    onProducerRemoved = NOOP,
    onConsumerAdded = NOOP,
    onConsumerRemoved = NOOP
  } = {}
) => {
  const allState = useParticipantTrackedState();
  const liveStateRef = useContext(ParticipantLiveStateContext);

  const selectParticipant = useCallback(
    s => s.participants?.get(participantId),
    [participantId]
  );
  const participant = useMeetingSelector(selectParticipant);
  const participantRef = useRef(participant);
  participantRef.current = participant;

  if (participant?.isAgent) {
    throw new Error(
      `useParticipant: "${participantId}" is an agent participant. Use useAgentParticipant() instead.`
    );
  }

  useStableEvent(
    `${events['participant-stream-enabled']}-${participantId}`,
    onStreamEnabled
  );
  useStableEvent(
    `${events['participant-stream-disabled']}-${participantId}`,
    onStreamDisabled
  );
  useStableEvent(
    `${events['participant-media-status-changed']}-${participantId}`,
    onMediaStatusChanged
  );
  useStableEvent(
    `${events['participant-video-quality-changed']}-${participantId}`,
    onVideoQualityChanged
  );
  useStableEvent(
    `${events['participant-stream-paused']}-${participantId}`,
    onStreamPaused
  );
  useStableEvent(
    `${events['participant-stream-resumed']}-${participantId}`,
    onStreamResumed
  );
  useStableEvent(
    `${events['participant-producer-added']}-${participantId}`,
    onProducerAdded
  );
  useStableEvent(
    `${events['participant-producer-removed']}-${participantId}`,
    onProducerRemoved
  );
  useStableEvent(
    `${events['participant-consumer-added']}-${participantId}`,
    onConsumerAdded
  );
  useStableEvent(
    `${events['participant-consumer-removed']}-${participantId}`,
    onConsumerRemoved
  );

  const setQuality = useCallback(q => {
    if (!participantRef.current) {
      return rejectUnavailable('setQuality', 'participant');
    }
    return participantRef.current.setQuality(q);
  }, []);
  // Synchronous — returns void, not a Promise. Left as optional-chaining.
  const setViewPort = useCallback((w, h) => {
    return participantRef.current?.setViewPort(w, h);
  }, []);
  const setScreenShareQuality = useCallback(q => {
    if (!participantRef.current) {
      return rejectUnavailable('setScreenShareQuality', 'participant');
    }
    return participantRef.current.setScreenShareQuality(q);
  }, []);
  const enableMic = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('enableMic', 'participant');
    }
    return participantRef.current.enableMic();
  }, []);
  const disableMic = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('disableMic', 'participant');
    }
    return participantRef.current.disableMic();
  }, []);
  const enableWebcam = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('enableWebcam', 'participant');
    }
    return participantRef.current.enableWebcam();
  }, []);
  const disableWebcam = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('disableWebcam', 'participant');
    }
    return participantRef.current.disableWebcam();
  }, []);
  const pin = useCallback(d => {
    if (!participantRef.current) {
      return rejectUnavailable('pin', 'participant');
    }
    return participantRef.current.pin(d);
  }, []);
  const unpin = useCallback(d => {
    if (!participantRef.current) {
      return rejectUnavailable('unpin', 'participant');
    }
    return participantRef.current.unpin(d);
  }, []);
  const remove = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('remove', 'participant');
    }
    return participantRef.current.remove();
  }, []);
  const captureImage = useCallback(({ height, width } = {}) => {
    if (!participantRef.current) {
      return rejectUnavailable('captureImage', 'participant');
    }
    return participantRef.current.captureImage({ height, width });
  }, []);
  const getAudioStats = useCallback(() => {
    return participantRef.current?.getAudioStats();
  }, []);
  const getVideoStats = useCallback(() => {
    return participantRef.current?.getVideoStats();
  }, []);
  const getTransportStats = useCallback(() => {
    return participantRef.current?.getTransportStats();
  }, []);
  const getShareStats = useCallback(() => {
    return participantRef.current?.getShareStats();
  }, []);
  const getShareAudioStats = useCallback(() => {
    return participantRef.current?.getShareAudioStats();
  }, []);
  const consumeWebcamStreams = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('consumeWebcamStreams', 'participant');
    }
    return participantRef.current.consumeWebcamStreams();
  }, []);
  const consumeMicStreams = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('consumeMicStreams', 'participant');
    }
    return participantRef.current.consumeMicStreams();
  }, []);
  const stopConsumingWebcamStreams = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('stopConsumingWebcamStreams', 'participant');
    }
    return participantRef.current.stopConsumingWebcamStreams();
  }, []);
  const stopConsumingMicStreams = useCallback(() => {
    if (!participantRef.current) {
      return rejectUnavailable('stopConsumingMicStreams', 'participant');
    }
    return participantRef.current.stopConsumingMicStreams();
  }, []);

  const methodsRef = useRef(null);
  methodsRef.current = {
    consumeMicStreams,
    consumeWebcamStreams,
    stopConsumingMicStreams,
    stopConsumingWebcamStreams,
    setQuality,
    setScreenShareQuality,
    setViewPort,
    enableMic,
    disableMic,
    enableWebcam,
    disableWebcam,
    captureImage,
    pin,
    unpin,
    remove,
    getAudioStats,
    getVideoStats,
    getShareStats,
    getShareAudioStats,
    getTransportStats
  };

  return useMemo(
    () =>
      new Proxy(allState, {
        get(target, prop, receiver) {
          if (prop in PARTICIPANT_STATE_KEYS) {
            const pState = target[participantId];
            const trackedValue = pState
              ? pState[prop]
              : EMPTY_PARTICIPANT_STATE[prop];
            // Return the latest committed value from the live ref so lazy
            // reads (event handlers, effects, timeouts) never see a stale
            // value captured from an earlier render.
            const live = liveStateRef && liveStateRef.current;
            const livePState = live ? live[participantId] : null;
            return livePState ? livePState[prop] : trackedValue;
          }
          const m = methodsRef.current;
          if (m && prop in m) {
            if (!participantRef.current) {
              return (...args) => {
                console.warn(
                  `useParticipant: ${String(
                    prop
                  )}() called but participant "${participantId}" not found.`
                );
              };
            }
            return m[prop];
          }
          return Reflect.get(target, prop, receiver);
        },
        has(target, prop) {
          return (
            prop in PARTICIPANT_STATE_KEYS ||
            !!(methodsRef.current && prop in methodsRef.current)
          );
        },
        ownKeys() {
          const stateKeys = Object.keys(PARTICIPANT_STATE_KEYS);
          const mKeys = Object.keys(methodsRef.current ?? {});
          return [...new Set([...stateKeys, ...mKeys])];
        },
        getOwnPropertyDescriptor() {
          return { configurable: true, enumerable: true, writable: false };
        }
      }),
    [allState, participantId, liveStateRef]
  );
};

export default useParticipant;
